import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScrollBar from 'react-custom-scrollbars';
import CommonDialog from './common/commonModal';
import ShareInput from './common/input';
import UxEventService from '../service/UxEventService';
import { isMobileDevice, EMAIL_REGEX, UXContextType } from '../commonUtil/util';
import { isMobile } from '../commonUtil/responsiveUtil';
import { withLinkStore } from '../app/reducers';
import { resetSessionTimer } from '../commonUtil/sessionUtil';
import { sharingLink } from '../service/BuyerExperienceService';
import { CautionIcon, CheckIcon, Questionicon } from './common/icons';
import ApplicationEntity from '../commonUtil/appEntity';
import { GDPR_DEFAULT_LINK, KeyBoardAction } from '../commonUtil/constants';
import DumpPrivacyLink from '../dumpComponent/dumpPrivacyLink';

const getModalStyle = () => {
    let shareDialogStyle = {
        content: {
            width: '534px',
            maxHeight: '96%',
            borderRadius: '8px'
        }
    };

    if (isMobileDevice.any() && isMobile()) {
        const content = {
            width: '100%',
            height: '100%',
            borderRadius: '0'
        };

        shareDialogStyle = {
            ...shareDialogStyle,
            content
        };
    }

    return shareDialogStyle;
};

const ShareEmailInput = ({ email, name, className, placeholder, isErrorInput, errorMsg, onChange, showErrorMsg, autoFocus }) => {
    const isValidEmail = EMAIL_REGEX.test(email.trim());

    const onBlur = () => {
        if (email) {
            const target = name === 'newRecipientEmailAddress' ? 'Update Recipient' : 'Update Sender Email';
            UxEventService.navigateViewEvent({ Context: UXContextType.SHARE_MODAL, Target: target });
        }
    };

    return (<div className="share-email-input">
        <ShareInput
            className={`${className} ${isValidEmail ? 'valid-email' : 'invalid-email'}`}
            autoFocus={autoFocus}
            value={email}
            name={name}
            placeholder={placeholder}
            hasError={isErrorInput}
            errorMsg={errorMsg}
            showErrorMsg={showErrorMsg}
            onBlur={onBlur}
            onChange={onChange} />
        { email && !isValidEmail && <CautionIcon /> }
        { email && isValidEmail && <CheckIcon /> }
    </div>);
};

const ShareModal = (props) => {
    const basicShareInfos = {
        sharingUserFirstName: '',
        sharingUserLastName: '',
        sharingUserEmailAddress: '',
        newRecipientEmailAddress: '',
        messageSubject: '',
        shortMessage: ''
    };
    const [shareInfos, setShareInfos] = useState(basicShareInfos);
    const [isSharing, setIsSharing] = useState(false);
    const [showRequiredMsg, setShowRequiredMsg] = useState(false);
    const { t } = useTranslation();
    const { viewerSetting, gdprSetting } = props.linkStore;
    const { brandingHilightColor } = viewerSetting;
    const { isOpen, setIsOpen, updateSelectLink } = props;

    const disableShareBtn = () => {
        // when all required fields have been fiiled out and the emails are valid, DO NOT disable the share button
        if (shareInfos.newRecipientEmailAddress && EMAIL_REGEX.test(shareInfos.newRecipientEmailAddress.trim())
            && shareInfos.sharingUserFirstName && shareInfos.sharingUserLastName
            && shareInfos.sharingUserEmailAddress && EMAIL_REGEX.test(shareInfos.sharingUserEmailAddress.trim())
            && shareInfos.messageSubject) {
            return false;
        }

        // else, disable the share button
        return true;
    };

    function isSubDomain(locationUrl) {
        const subdomain = `https://${ApplicationEntity.getInstance().tenant}.ls.seismic.com`;
        return locationUrl.includes(subdomain);
    }

    const onClickShare = async () => {
        if (disableShareBtn()) {
            setShowRequiredMsg(true);
            return;
        }

        setIsSharing(true);
        const sharingLinkRequest = {
            ...shareInfos,
            sharingUserEmailAddress: shareInfos.sharingUserEmailAddress.trim(),
            newRecipientEmailAddress: shareInfos.newRecipientEmailAddress.trim(),
            sessionId: ApplicationEntity.getInstance().sessionId,
            isSubDomain: isSubDomain(window.location.href)
        };
        const shareResult = await sharingLink(sharingLinkRequest, ApplicationEntity.getInstance().tenant);
        setIsSharing(false);
        if (shareResult && shareResult.result) {
            props.onShareSuccess();
        } else {
            console.error('shareResult', shareResult);
            props.onShareFailed(shareResult, shareInfos);
        }

        UxEventService.navigateViewEvent({ Context: UXContextType.SHARE_MODAL, Target: 'Share Link Button' });
    };

    const onShareInfoChange = (key, value) => {
        setShareInfos({
            ...shareInfos,
            [key]: value
        });
    };

    const onShareInputBlur = (name, value) => {
        if (value) {
            let target = 'Update Sender Name';
            if (name === 'messageSubject') {
                target = 'Update Email Subject';
            }

            UxEventService.navigateViewEvent({ Context: UXContextType.SHARE_MODAL, Target: target });
        }
    };

    const onMessageChange = (e) => {
        const shortMessage = e.target && e.target.value;
        setShareInfos({
            ...shareInfos,
            shortMessage
        });
    };

    const onMessageBoxBlur = () => {
        if (shareInfos.shortMessage) {
            UxEventService.navigateViewEvent({ Context: UXContextType.SHARE_MODAL, Target: 'Update Email Body' });
        }
    };

    const isErrorInput = ({ name, value }) => {
        if ((name === 'newRecipientEmailAddress' || name === 'sharingUserEmailAddress') && value) {
            if (EMAIL_REGEX.test(value.trim())) {
                return true;
            }

            return false;
        }

        if (showRequiredMsg) {
            return !!value;
        }

        return true;
    };


    const showErrorMsgForRequired = ({ name, value }) => {
        // this function is used for the sender info inputs to determine whether the error message need to show up
        if (name === 'sharingUserEmailAddress') {
            const { sharingUserFirstName, sharingUserLastName, sharingUserEmailAddress } = shareInfos;
            if (value && !EMAIL_REGEX.test(value.trim())) {
                return true;
            }

            if (showRequiredMsg) {
                return !sharingUserFirstName || !sharingUserLastName || !sharingUserEmailAddress;
            }
        }

        return false;
    };

    const getSenderInfoErrorMsg = () => {
        // this is used for the sender email input to determine which error message to use
        const { sharingUserFirstName, sharingUserLastName, sharingUserEmailAddress } = shareInfos;
        if (!sharingUserFirstName || !sharingUserLastName || !sharingUserEmailAddress) {
            return t('LIVE_SEND_VIEWER_SHARE_SENDER_REQUIRED');
        }

        if (!EMAIL_REGEX.test(sharingUserEmailAddress.trim())) {
            return t('LIVE_SEND_VIEWER_SHARE_INVALID_EMAIL');
        }

        return '';
    };

    const onCilckShareModal = (e) => {
        e.stopPropagation();
        resetSessionTimer();
    };

    const onCancelShare = () => {
        UxEventService.navigateViewEvent({ Context: UXContextType.SHARE_MODAL, Target: 'Cancel Share' });
        props.setIsOpen(false);
    };

    const privacyLink = gdprSetting.privacyLink;
    const onOpenPrivacyLink = () => {
        if (privacyLink) {
            UxEventService.navigateViewEvent({ Context: UXContextType.SHARE_MODAL, Target: 'Privacy Link' });
        }
    };

    const onCancelKeyDown = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            onCancelShare();
        }
    };

    const onShareKeyDown = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            onClickShare();
        }
    };

    const validRecipientEmail = EMAIL_REGEX.test(shareInfos.newRecipientEmailAddress.trim());

    let shareBtnStyle = { borderColor: brandingHilightColor, backgroundColor: brandingHilightColor };
    let sharBtnClass = '-enable';
    if (isSharing || (showRequiredMsg && disableShareBtn())) {
        shareBtnStyle = {};
        sharBtnClass = '-disable';
    }

    return (
        <CommonDialog customStyles={getModalStyle()}
            shouldCloseOnOverlayClick={false}
            showCloseIcon
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            afterCloseModal={onCancelShare}
            className={`${isMobileDevice.any() ? 'engagement-viewer-mobile-device' : ''} seismic-engagement-viewer-share-dialog`}>
            <div className="seismic-engagement-viewer-share-dialog-container" onClick={onCilckShareModal} >
                {isSharing && <div className="share-modal-overlay">
                    <div className="engagement-viewer-busy-spinner" />
                </div>}
                <div className="seismic-engagement-viewer-share-dialog-header">
                    <div className="share-dialog-header-title">{t('LIVE_SEND_VIEWER_SHARE_LINK')}</div>
                </div>
                <div className="seismic-engagement-viewer-share-dialog-content">
                    <ScrollBar autoHide autoHeight autoHeightMax={'calc(96vh - 112px)'}>
                        <div className="share-dialog-content-scroll-box">
                            <div className="share-dialog-content-recipient">
                                <label htmlFor="newRecipientEmailAddress" className="sharing-with-label">{t('LIVE_SEND_VIEWER_SHARE_WITH')}</label>
                                <ShareEmailInput
                                    className="sharing-with-input"
                                    autoFocus
                                    email={shareInfos.newRecipientEmailAddress}
                                    name="newRecipientEmailAddress"
                                    placeholder={t('LIVE_SEND_VIEWER_SHARE_RECIPIENT_EMAIL')}
                                    isErrorInput={isErrorInput}
                                    errorMsg={(!validRecipientEmail && shareInfos.newRecipientEmailAddress) ? t('LIVE_SEND_VIEWER_SHARE_INVALID_EMAIL') : t('LIVE_SEND_VIEWER_SHARE_RECIPIENT_REQUIRED')}
                                    onChange={onShareInfoChange} />
                            </div>
                            <div className="share-dialog-content-sender">
                                <div className="sender-section">
                                    <label htmlFor="senderInfos" className="sender-sction-title">{t('LIVE_SEND_VIEWER_SHARE_SENT_FROM')}</label>
                                    <div className="sender-supplement-question-mark">
                                        <Questionicon className={props.className} />
                                        <div className="message-popover">
                                            <div className="arrow" />
                                            <div className="message-popover-body"> {t('LIVE_SEND_VIEWER_SHARE_SENDER_INFO_SUPPLEMENT')} </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="sender-name">
                                    <ShareInput
                                        className="sender-first-name"
                                        value={shareInfos.sharingUserFirstName}
                                        name="sharingUserFirstName"
                                        placeholder={t('LIVE_SEND_VIEWER_SHARE_FIRST_NAME')}
                                        hasError={isErrorInput}
                                        onBlur={onShareInputBlur}
                                        onChange={onShareInfoChange} />
                                    <ShareInput
                                        className="sender-last-name"
                                        value={shareInfos.sharingUserLastName}
                                        name="sharingUserLastName"
                                        placeholder={t('LIVE_SEND_VIEWER_SHARE_LAST_NAME')}
                                        hasError={isErrorInput}
                                        onBlur={onShareInputBlur}
                                        onChange={onShareInfoChange} />
                                </div>
                                <ShareEmailInput
                                    className="sender-email-address"
                                    email={shareInfos.sharingUserEmailAddress}
                                    name="sharingUserEmailAddress"
                                    placeholder={t('LIVE_SEND_VIEWER_SHARE_EMAIL')}
                                    isErrorInput={isErrorInput}
                                    errorMsg={getSenderInfoErrorMsg()}
                                    showErrorMsg={showErrorMsgForRequired}
                                    onChange={onShareInfoChange} />
                            </div>
                            <div className="share-dialog-content-message">
                                <label htmlFor="messageSubject" className="share-message-label">{t('LIVE_SEND_VIEWER_SHARE_EMAIL_SUBJECT')}</label>
                                <ShareInput
                                    className="share-subject-input"
                                    value={shareInfos.messageSubject}
                                    name="messageSubject"
                                    placeholder={t('LIVE_SEND_VIEWER_SHARE_ADD_SUBJECT')}
                                    hasError={isErrorInput}
                                    errorMsg={t('LIVE_SEND_VIEWER_SHARE_SUNJECT_REQUIRED')}
                                    onBlur={onShareInputBlur}
                                    onChange={onShareInfoChange} />
                                <textarea className="share-message-textarea"
                                    value={shareInfos.shortMessage}
                                    aria-label={t('LIVE_SEND_VIEWER_SHARE_INCLUDE_MESSAGE')}
                                    placeholder={t('LIVE_SEND_VIEWER_SHARE_INCLUDE_MESSAGE')}
                                    onBlur={onMessageBoxBlur}
                                    onChange={onMessageChange} />
                            </div>
                            <div className="share-dialog-disclaimer-message">
                                <span className="disclaimer-message-text">{t('LIVE_SEND_VIEWER_SHARE_DISCLAIMER_MESSAGE')}</span>&nbsp;
                                { privacyLink && privacyLink.length !== 0 ?
                                    <DumpPrivacyLink
                                        clickHandler={onOpenPrivacyLink}
                                        updateSelectLink={updateSelectLink}
                                        privacyLinkUrl={privacyLink}
                                        privacyPolicyText={t('LIVE_SEND_PRIVACY_POLICY')} /> :
                                    <a className="privacy-policy-link"
                                        rel="noopener noreferrer"
                                        href={GDPR_DEFAULT_LINK}
                                        target="_blank"
                                        onClick={onOpenPrivacyLink}>
                                        {t('LIVE_SEND_PRIVACY_POLICY')}
                                    </a>
                                }
                                .
                            </div>
                        </div>
                    </ScrollBar>

                </div>
                <div className="seismic-engagement-viewer-share-dialog-footer">
                    <button className="enagement-buyer-btn btn-active btn-cancel"
                        style={{ borderColor: brandingHilightColor, color: brandingHilightColor }}
                        onKeyDown={onCancelKeyDown}
                        onClick={onCancelShare}>
                        {t('LIVE_SEND_VIEWER_CANCEL')}
                    </button>
                    <button className={`enagement-buyer-btn btn-active btn-share ${sharBtnClass}`}
                        style={shareBtnStyle}
                        onKeyDown={onShareKeyDown}
                        onClick={onClickShare}>
                        {t('LIVE_SEND_VIEWER_SHARE')}
                    </button>
                </div>
            </div>
        </CommonDialog>
    );
};

export default withLinkStore(ShareModal);
